import { Component, Inject, Output, EventEmitter, OnInit } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IOrganisationsHierarchy } from '@services/organisation-data.service';
import { GlobalFunctions, SnackType, imagePath, imgTypes } from '@global/GlobalFunctions';
import { takeUntil, distinctUntilChanged, finalize, filter } from 'rxjs/operators';
import { FilesService } from '@services/files.service';
import { ChangeDetectorRef } from '@angular/core';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Account, IAccount } from '@services/accounts.service';
import { ConfirmDialogComponent } from 'src/app/dialogs/confirm-dialog/confirm-dialog.component';

/**
 * Component
 */
@Component({
	selector: 'app-edit-hierarchy-location',
	templateUrl: './edit-hierarchy-location.component.html',
	styleUrls: ['./edit-hierarchy-location.component.scss']
})
export class EditHierarchyLocationComponent implements OnInit {

	/** Path to Sub Location Images */
	public imagePathSubLocations(accountGUID: string): string {
		return imagePath(accountGUID, imgTypes.sublocations).toLowerCase()
	}

	/** Path to Organisation Images */
	public imagePathOrganisations(accountGUID: string): string {
		return imagePath(accountGUID, imgTypes.organisations).toLowerCase()
	}

	/** Update the Image */
	@Output() imageUpdated = new EventEmitter<any>();

	/** Update Image Instantly */
	// public numberRND: number = Math.random();
	/** new Image Selected */
	public newImage: boolean;

	/** Form Group */
	dialogForm: FormGroup;

	/** Enable the New Picture Button */
	public enablePicture: boolean = true;
	/** Enable the Camera Button */
	public enableCamera: boolean = true;
	/** Enable the Delete Button */
	public enableDelete: boolean = false;

	/** File */
	public file: any
	/** Filename */
	public fileName: string
	/** Loading */
	public loading: boolean
	/** shortLink */
	public shortLink: any

	/** selected Account */
	public selectedAccount: IAccount;
	/** selected AccountGUID */
	public selectedAccountGUID: string
	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	imageSrc: string | null = null; // This will hold the image URL for binding

	/**
	 * Default Constructor
	 * @param fb 
	 * @param dialogRef Referense to the Dialog box
	 * @param filesService 
	 * @param globalFunctions Global Functions
	 * @param siteSubscriptions 
	 * @param dialogData object interface
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<EditHierarchyLocationComponent>,
		private filesService: FilesService,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions,
		@Inject(MAT_DIALOG_DATA) public dialogData: IOrganisationsHierarchy,
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([account]) => !!account)
		).subscribe({
			next: ([account]) => {
				this.selectedAccount = account;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.selectedAccountGUID = this.selectedAccount.GUID.toLowerCase();
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false); }
		});
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {

		this.dialogForm = this.fb.group({
			Id: [this.dialogData.Id || 0],
			Uid: [this.dialogData.Uid || ''],
			Name: [this.dialogData.Name || '', Validators.required],
			Description: [this.dialogData.Description || ''],
			ImageURL: [this.dialogData.ImageURL || ''],
			tempImageURL: [this.dialogData.ImageURL || ''],
			FunctionalLocation: [this.dialogData.FunctionalLocation || ''],
			ParentFunctionalLocation: [this.dialogData.ParentFunctionalLocation || '']
		});

		if (this.dialogData.ParentUid) {
			this.imageSrc = imagePath(this.selectedAccountGUID, imgTypes.sublocations) + this.dialogData.Id + '?' + this.dialogData.Modified
		} else {
			this.imageSrc = imagePath(this.selectedAccountGUID, imgTypes.organisations) + this.dialogData.Id + '?' + this.dialogData.Modified
		}
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * Delete Image
	 */
	deleteImg(): void {
		// Extract and store the file name
		this.fileName = "transparent.png";
		this.imageSrc = "";
		this.file = new File([""], "transparent.png", { type: "image/png" });
		this.dialogForm.markAsDirty();
		this.newImage = true;
	}

	/**
		 * New File deteced
		 * @param file File from child component
		 */
	onNewImage(file: File | null): void {

		/** Check for a valid file */
		if (file) {
			this.file = file;
			this.newImage = true

		}
		else {
			this.file = undefined;
			this.newImage = false;
		}
		this.dialogForm.markAsDirty();
	}

	/**
	 * Save the form data and image if needed
	 */
	async onSaveClick(): Promise<void> {

		if (this.newImage && this.file) {
			try {
				const accountGUID = this.selectedAccountGUID.toLowerCase();
				const fileName = this.dialogData.Id.toString();
				const location = "locations";
				await this.filesService.saveImage(accountGUID, fileName, this.file, location); // wait for upload to complete
				this.newImage = false;
				this.file = undefined;
			} // Optional: handle upload failure (maybe prevent closing the dialog)
			catch (error) { return; }
		}

		// Now the upload is done, close the dialog with the form value
		this.dialogRef.close(this.dialogForm.value);
	}


}
